/* Admin-side data helpers: candidate list cache + per-candidate details cache. */

import { useEffect, useState } from 'react';
import { exportCandidates, getCandidateDetails, listCandidates } from '@shared/api/adminApi.ts';
import type { Candidate, CandidateDetails, ExportRange } from '@shared/types.ts';

/* --------------------------- candidate list ------------------------------ */
// GET /api/admin/candidates returns everyone in one array, so the portal pages
// through it client-side. One shared promise avoids refetching when switching tabs.
let listPromise: Promise<Candidate[]> | null = null;

export function fetchCandidates(force = false): Promise<Candidate[]> {
  if (force || !listPromise) {
    listPromise = listCandidates().catch((e) => {
      listPromise = null;
      throw e;
    });
  }
  return listPromise;
}
export function invalidateCandidates() {
  listPromise = null;
}

/* ------------------------------ details ---------------------------------- */
// Category details (school / college / work) are a separate call per candidate.
const detailsCache = new Map<string, Promise<CandidateDetails>>();

export function fetchDetails(c: Pick<Candidate, 'id' | 'candidate_type'>): Promise<CandidateDetails> {
  let p = detailsCache.get(c.id);
  if (!p) {
    p = getCandidateDetails(c.id, c.candidate_type).catch((e) => {
      detailsCache.delete(c.id);
      throw e;
    });
    detailsCache.set(c.id, p);
  }
  return p;
}
export function invalidateDetails(id: string) {
  detailsCache.delete(id);
}

/**
 * Loads details for the rows on screen (max 6 requests in flight).
 * Returns a map id → details | 'error'; missing keys are still loading.
 */
export function useRowDetails(rows: Candidate[]) {
  const [map, setMap] = useState<Record<string, CandidateDetails | 'error'>>({});
  const key = rows.map((r) => r.id).join(',');

  useEffect(() => {
    let live = true;
    const queue = [...rows];
    const worker = async () => {
      while (live && queue.length) {
        const c = queue.shift()!;
        try {
          const d = await fetchDetails(c);
          if (live) setMap((m) => (m[c.id] === d ? m : { ...m, [c.id]: d }));
        } catch {
          if (live) setMap((m) => ({ ...m, [c.id]: 'error' }));
        }
      }
    };
    for (let i = 0; i < 6; i++) worker();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return map;
}

/* ------------------------------- export ---------------------------------- */

export const EXPORTS: { range: ExportRange; label: string; hint: string }[] = [
  { range: 'daily', label: 'आज नोंदणी झालेले', hint: 'गेल्या २४ तासांतील नोंदणी' },
  { range: 'weekly', label: 'या आठवड्यात नोंदणी झालेले', hint: 'गेल्या ७ दिवसांतील नोंदणी' },
  { range: 'total', label: 'एकूण सर्व नोंदणी', hint: 'आतापर्यंतचे सर्व स्पर्धक' },
];

/** Downloads the server-built .xlsx and triggers a browser save. */
export async function downloadExport(range: ExportRange) {
  const { blob, filename } = await exportCandidates(range);
  const date = new Date().toISOString().slice(0, 10);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename ?? `candidates_${range}_${date}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/* ------------------------------ counting --------------------------------- */

export function countSince(list: Candidate[], ms: number): number {
  const cut = Date.now() - ms;
  return list.filter((c) => Date.parse(c.created_at) >= cut).length;
}
